'use client';

import { useState, useEffect } from 'react';
import ServiceCard from './ServiceCard';
import ComplianceMatrix from './ComplianceMatrix';
import PricingCard from './PricingCard';
import VerticalCompliance from './VerticalCompliance';
import ServiceComparison from './ServiceComparison';
import Icon from '@/components/ui/AppIcon';

interface Service {
  icon: string;
  title: string;
  description: string;
  features: string[];
  compliance: string;
}

interface ComplianceItem {
  jurisdiction: string;
  status: 'compliant' | 'partial' | 'restricted';
  requirements: string[];
  documentation: string;
}

interface PricingTier {
  name: string;
  description: string;
  prepaidAmount: string;
  features: string[];
  limitations: string[];
  recommended?: boolean;
}

interface VerticalItem {
  name: string;
  riskLevel: 'high' | 'medium' | 'low';
  requirements: string[];
  platforms: string[];
  restrictions: string[];
}

interface ComparisonFeature {
  name: string;
  basic: boolean | string;
  professional: boolean | string;
  enterprise: boolean | string;
}

export default function ServicesInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeSection, setActiveSection] = useState<'overview' | 'pricing' | 'technical' | 'compliance'>('overview');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: Service[] = [
    {
      icon: 'ServerIcon',
      title: 'White-Label Account Infrastructure',
      description: 'Enterprise-grade advertising account provisioning with full regulatory compliance and platform policy alignment.',
      features: [
        'Meta Business Manager account setup and configuration',
        'Google Ads account provisioning with compliance verification',
        'Multi-platform integration with unified billing',
        'Account health monitoring and policy compliance tracking',
        'Dedicated account manager for technical support',
      ],
      compliance: 'Hong Kong SAR Regulatory Framework',
    },
    {
      icon: 'ShieldCheckIcon',
      title: 'High-Risk Vertical Compliance',
      description: 'Specialized infrastructure for regulated industries requiring enhanced compliance protocols and documentation.',
      features: [
        'Financial services advertising compliance frameworks',
        'Healthcare and pharmaceutical regulatory alignment',
        'Gambling and gaming jurisdiction-specific protocols',
        'Cryptocurrency and blockchain advertising infrastructure',
        'Real-time policy update monitoring and adaptation',
      ],
      compliance: 'Multi-Jurisdiction Compliance Certified',
    },
    {
      icon: 'CreditCardIcon',
      title: 'Prepaid Fund Management',
      description: 'Transparent prepaid billing infrastructure with no hidden fees, performance guarantees, or opaque pricing structures.',
      features: [
        'Prepaid account funding with transparent fee structure',
        'Real-time spend tracking and budget management',
        'Multi-currency support with competitive exchange rates',
        'Automated billing reconciliation and reporting',
        'No performance-based fees or hidden charges',
      ],
      compliance: 'Financial Transparency Standards',
    },
    {
      icon: 'DocumentTextIcon',
      title: 'Platform Integration Documentation',
      description: 'Comprehensive technical documentation for seamless integration with advertising platforms and compliance systems.',
      features: [
        'API integration guides for major advertising platforms',
        'Compliance documentation templates and frameworks',
        'Technical specification sheets for enterprise systems',
        'Integration testing protocols and validation procedures',
        'Ongoing documentation updates for platform changes',
      ],
      compliance: 'Technical Standards Compliance',
    },
    {
      icon: 'ChartBarIcon',
      title: 'Performance Infrastructure',
      description: 'Reliable technical infrastructure for campaign execution without performance guarantees or outcome promises.',
      features: [
        'High-availability platform access with 99.9% uptime SLA',
        'Campaign deployment infrastructure with quality checks',
        'Technical performance monitoring and alerting',
        'Infrastructure optimization recommendations',
        'No performance guarantees - infrastructure reliability only',
      ],
      compliance: 'Service Level Agreement Certified',
    },
    {
      icon: 'UserGroupIcon',
      title: 'Enterprise Support Services',
      description: 'Professional support infrastructure for enterprise clients requiring institutional-grade service delivery.',
      features: [
        'Dedicated account management team assignment',
        'Priority technical support with guaranteed response times',
        'Quarterly compliance review and documentation updates',
        'Custom integration support for enterprise systems',
        'Professional consultation on regulatory changes',
      ],
      compliance: 'Enterprise Service Standards',
    },
  ];

  const complianceMatrix: ComplianceItem[] = [
    {
      jurisdiction: 'Hong Kong SAR',
      status: 'compliant',
      requirements: ['Business registration', 'Financial licensing', 'Data protection compliance'],
      documentation: 'View Framework',
    },
    {
      jurisdiction: 'United States',
      status: 'compliant',
      requirements: ['GDPR alignment', 'CCPA compliance', 'Platform policy adherence'],
      documentation: 'View Requirements',
    },
    {
      jurisdiction: 'European Union',
      status: 'compliant',
      requirements: ['GDPR full compliance', 'DSA alignment', 'Local advertising regulations'],
      documentation: 'View Standards',
    },
    {
      jurisdiction: 'United Kingdom',
      status: 'compliant',
      requirements: ['UK GDPR', 'ASA guidelines', 'Financial promotions compliance'],
      documentation: 'View Guidelines',
    },
    {
      jurisdiction: 'Australia',
      status: 'partial',
      requirements: ['Privacy Act compliance', 'ACCC guidelines', 'Platform-specific requirements'],
      documentation: 'View Details',
    },
    {
      jurisdiction: 'Singapore',
      status: 'compliant',
      requirements: ['PDPA compliance', 'MAS regulations', 'IMDA guidelines'],
      documentation: 'View Framework',
    },
  ];

  const pricingTiers: PricingTier[] = [
    {
      name: '🚀 Launch',
      description: 'For individuals getting started with digital advertising',
      prepaidAmount: '$0/mo',
      features: [
        'Single platform account provisioning',
        'Standard compliance documentation',
        'Chat board help via dashboard',
        'Basic technical integration support',
        'Access to any 1 platform (Meta / Google / TikTok)',
      ],
      limitations: [
        'No access to multiple platforms',
        'URLs and content need approval',
        'Maximum 5 ads accounts per month',
        'Support team response within 24 hours',
      ],
    },
    {
      name: '📈 Growth',
      description: 'For growing businesses running multi-channel campaigns',
      prepaidAmount: '$300/mo',
      features: [
        'Multi-platform account provisioning',
        'Access to WhatsApp / Telegram group',
        '12-hour turnaround time',
        'Basic technical integration support',
        'Cross-platform budget allocation',
        'Ad account onboarding guide',
      ],
      limitations: [
        'No access to native advertising platforms',
        'Lower top-up charges than 🚀 Launch Plan',
        'Maximum 20 ads accounts per month',
      ],
    },
    {
      name: '⚡ Scale',
      description: 'Advanced support for agencies and scaling brands',
      prepaidAmount: '$500/mo',
      features: [
        'All platform account provisioning (including native)',
        'Access to WhatsApp / Telegram group',
        'Priority support with ~8-hour response',
        'Dedicated account manager',
        'All platform access',
      ],
      limitations: [
        'No dedicated strategist',
        'Standard compliance documentation',
        'Email support with 48-hour response',
        'Maximum 40 ads accounts per month',
      ],
      recommended: true,
    },
    {
      name: '💎 Dominance',
      description: 'Institutional-grade service with full compliance support',
      prepaidAmount: '$1,000/mo',
      features: [
        'All features from 📈 Growth Plan',
        'Fast support with 1–2 hour response',
        'Dedicated ads expert',
        'Help in creatives',
        'Ads optimization consultancy (4 hours/month)',
        'Unlimited ad accounts',
        'Strategic campaign review',
        'Customized reports',
        'Custom workflow setup & AI tools support',
        'Fast-track account provisioning',
      ],
      limitations: [
        'No performance guarantees provided',
      ],
    },
    {
      name: '👑 Media Buyer Scales++',
      description: 'For agencies, media buying teams & high-volume advertisers requiring dedicated operational support',
      prepaidAmount: '$15,000/mo',
      features: [
        'All features from 💎 Dominance Plan',
        'Free white-label reporting suite (agency branded dashboard)',
        'Lower top-up charges (B2B rates)',
        'Preferred enterprise pricing',
        'Priority payment processing',
        'Consolidated billing options',
        'Volume-based discounts',
        'Enterprise credit facilities (subject to approval)',
        'Client onboarding assistance',
        'Agency scaling consultation',
        'Agency process optimization',
        'Team training sessions',
        'SOP development guidance',
        'Client retention strategy reviews',
        'Growth opportunity assessments',
        'Revenue expansion consultation',
      ],
      limitations: [
        'No performance guarantees provided',
        'Subject to enterprise onboarding process',
      ],
    },
  ];

  const verticals: VerticalItem[] = [
    {
      name: 'Financial Services & Investment Products',
      riskLevel: 'high',
      requirements: [
        'Securities licensing documentation',
        'Financial regulatory compliance certificates',
        'Risk disclosure statement approval',
        'Jurisdiction-specific advertising permits',
      ],
      platforms: ['Meta (Restricted)', 'Google Ads (Certified Required)', 'LinkedIn (Approved)'],
      restrictions: [
        'No guaranteed returns or performance claims',
        'Mandatory risk disclosures on all creatives',
        'Geographic targeting restrictions apply',
        'Enhanced compliance monitoring required',
      ],
    },
    {
      name: 'Healthcare & Pharmaceutical',
      riskLevel: 'high',
      requirements: [
        'Medical licensing verification',
        'FDA/EMA compliance documentation',
        'Healthcare advertising certifications',
        'Product registration evidence',
      ],
      platforms: ['Google Ads (Healthcare Certified)', 'Meta (Restricted)', 'Bing Ads (Approved)'],
      restrictions: [
        'No disease cure or treatment guarantees',
        'Mandatory medical disclaimers required',
        'Prescription drug advertising limitations',
        'Age and geographic restrictions apply',
      ],
    },
    {
      name: 'Gambling & Gaming',
      riskLevel: 'high',
      requirements: [
        'Gaming license from recognized jurisdiction',
        'Responsible gambling certifications',
        'Age verification system documentation',
        'Financial transaction compliance proof',
      ],
      platforms: ['Google Ads (Licensed Jurisdictions)', 'Meta (Approved Markets)', 'Twitter (Restricted)'],
      restrictions: [
        'Geographic restrictions by jurisdiction',
        'Mandatory responsible gambling messaging',
        'Age targeting minimum 21+ required',
        'No bonus or promotional guarantees',
      ],
    },
    {
      name: 'Cryptocurrency & Blockchain',
      riskLevel: 'high',
      requirements: [
        'Cryptocurrency exchange licensing',
        'Financial services registration',
        'AML/KYC compliance documentation',
        'Regulatory approval from target markets',
      ],
      platforms: ['Google Ads (Certified Only)', 'Meta (Restricted)', 'LinkedIn (B2B Approved)'],
      restrictions: [
        'No investment return guarantees',
        'Mandatory risk disclosure statements',
        'Geographic targeting highly restricted',
        'ICO/token sale advertising prohibited',
      ],
    },
    {
      name: 'Real Estate & Property',
      riskLevel: 'medium',
      requirements: [
        'Real estate licensing verification',
        'Fair housing compliance certification',
        'Property disclosure documentation',
        'Local advertising regulation compliance',
      ],
      platforms: ['Meta (Standard)', 'Google Ads (Standard)', 'LinkedIn (Approved)'],
      restrictions: [
        'Fair housing law compliance mandatory',
        'No discriminatory targeting allowed',
        'Property disclosure requirements vary by jurisdiction',
        'Rental advertising subject to local laws',
      ],
    },
    {
      name: 'E-commerce & Retail',
      riskLevel: 'low',
      requirements: [
        'Business registration documentation',
        'Consumer protection compliance',
        'Return policy documentation',
        'Payment processing verification',
      ],
      platforms: ['Meta (Standard)', 'Google Ads (Standard)', 'All Major Platforms'],
      restrictions: [
        'Product claims must be substantiated',
        'Pricing accuracy required',
        'Shipping disclosure mandatory',
        'Consumer protection laws apply',
      ],
    },
  ];

  const comparisonFeatures: ComparisonFeature[] = [
    { name: 'Platform Account Provisioning', basic: '1 Platform', professional: '3 Platforms', enterprise: 'Unlimited' },
    { name: 'Compliance Documentation', basic: true, professional: true, enterprise: true },
    { name: 'High-Risk Vertical Access', basic: false, professional: 'Limited', enterprise: 'Full Access' },
    { name: 'Dedicated Account Manager', basic: false, professional: false, enterprise: true },
    { name: 'API Integration Support', basic: 'Standard', professional: 'Priority', enterprise: 'Custom' },
    { name: 'Support Response Time', basic: '48 hours', professional: '24 hours', enterprise: '4 hours' },
    { name: 'Compliance Monitoring', basic: 'Monthly', professional: 'Weekly', enterprise: 'Real-time' },
    { name: 'Custom Framework Development', basic: false, professional: false, enterprise: true },
    { name: 'Technical Documentation', basic: true, professional: true, enterprise: true },
    { name: 'Performance Guarantees', basic: false, professional: false, enterprise: false },
  ];

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-32 pb-16 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse space-y-8">
              <div className="h-12 bg-muted rounded w-3/4"></div>
              <div className="h-6 bg-muted rounded w-1/2"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-64 bg-muted rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-32 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="ServerIcon" size={32} variant="outline" className="text-primary" />
              <h1 className="font-headline font-bold text-4xl lg:text-5xl text-primary">
                Infrastructure Services
              </h1>
            </div>
            <p className="font-body text-lg text-text-secondary max-w-3xl leading-relaxed">
              Enterprise-grade advertising infrastructure with comprehensive compliance frameworks. No performance guarantees - only reliable technical systems and regulatory alignment.
            </p>
          </div>

          <div className="mb-12 border-b border-border">
            <div className="flex items-center space-x-2 overflow-x-auto">
              <button
                onClick={() => setActiveSection('overview')}
                className={`px-6 py-3 font-headline font-semibold text-sm whitespace-nowrap transition-colors duration-smooth ${
                  activeSection === 'overview' ?'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-primary'
                }`}
              >
                Service Overview
              </button>
              <button
                onClick={() => setActiveSection('pricing')}
                className={`px-6 py-3 font-headline font-semibold text-sm whitespace-nowrap transition-colors duration-smooth ${
                  activeSection === 'pricing' ?'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-primary'
                }`}
              >
                Pricing Structure
              </button>
              <button
                onClick={() => setActiveSection('compliance')}
                className={`px-6 py-3 font-headline font-semibold text-sm whitespace-nowrap transition-colors duration-smooth ${
                  activeSection === 'compliance' ?'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-primary'
                }`}
              >
                Compliance Framework
              </button>
            </div>
          </div>

          {activeSection === 'overview' && (
            <div className="space-y-16">
              <div>
                <h2 className="font-headline font-bold text-3xl text-primary mb-8">
                  Core Infrastructure Services
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {services.map((service, index) => (
                    <ServiceCard key={index} {...service} />
                  ))}
                </div>
              </div>

              <div className="bg-surface border border-border rounded-sm p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <Icon name="ExclamationTriangleIcon" size={24} variant="solid" className="text-warning flex-shrink-0" />
                  <div>
                    <h3 className="font-headline font-semibold text-xl text-primary mb-2">
                      Service Limitations & Transparency
                    </h3>
                    <p className="font-body text-sm text-text-secondary leading-relaxed mb-4">
                      Danny Clans Media provides infrastructure services only. We do not guarantee advertising performance, campaign outcomes, or return on investment. Our service focuses on reliable technical systems, regulatory compliance, and platform access.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start space-x-2">
                        <Icon name="MinusIcon" size={16} variant="outline" className="text-text-secondary flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-secondary">No performance guarantees or outcome promises</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="MinusIcon" size={16} variant="outline" className="text-text-secondary flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-secondary">Infrastructure reliability only - campaign success depends on client execution</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="MinusIcon" size={16} variant="outline" className="text-text-secondary flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-secondary">Compliance frameworks provided - client responsible for adherence</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="MinusIcon" size={16} variant="outline" className="text-text-secondary flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-secondary">Platform policy changes may affect service availability</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-headline font-bold text-3xl text-primary mb-8">
                  Service Comparison Matrix
                </h2>
                <ServiceComparison features={comparisonFeatures} />
              </div>
            </div>
          )}

          {activeSection === 'pricing' && (
            <div className="space-y-16">
              <div>
                <h2 className="font-headline font-bold text-3xl text-primary mb-4">
                  Transparent Prepaid Pricing
                </h2>
                <p className="font-body text-base text-text-secondary max-w-3xl leading-relaxed mb-8">
                  All services operate on a prepaid model with transparent fee structures. No hidden charges, performance-based fees, or opaque pricing. Funds are held in segregated accounts with full transparency.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {pricingTiers.map((tier, index) => (
                    <PricingCard key={index} tier={tier} />
                  ))}
                </div>
              </div>

              <div className="bg-card border border-border rounded-sm p-8">
                <h3 className="font-headline font-semibold text-xl text-primary mb-6">
                  Prepaid Fund Management
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-headline font-semibold text-sm text-primary mb-3">
                      How Prepaid Funds Work
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-primary">Funds deposited to segregated Hong Kong bank account</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-primary">Real-time spend tracking and balance monitoring</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-primary">Transparent fee structure with no hidden charges</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-primary">Monthly reconciliation and detailed reporting</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-headline font-semibold text-sm text-primary mb-3">
                      What We Don't Do
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start space-x-2">
                        <Icon name="XMarkIcon" size={16} variant="solid" className="text-error flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-secondary">No performance-based fees or hidden charges</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="XMarkIcon" size={16} variant="solid" className="text-error flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-secondary">No minimum spend requirements or commitments</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="XMarkIcon" size={16} variant="solid" className="text-error flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-secondary">No opaque pricing or variable fee structures</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="XMarkIcon" size={16} variant="solid" className="text-error flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-secondary">No fund commingling or unclear accounting</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'compliance' && (
            <div className="space-y-16">
              <div>
                <h2 className="font-headline font-bold text-3xl text-primary mb-8">
                  Global Compliance Framework
                </h2>
                <ComplianceMatrix items={complianceMatrix} />
              </div>

              <div>
                <h2 className="font-headline font-bold text-3xl text-primary mb-8">
                  High-Risk Vertical Compliance
                </h2>
                <VerticalCompliance verticals={verticals} />
              </div>

              <div className="bg-surface border border-border rounded-sm p-8">
                <div className="flex items-start space-x-4">
                  <Icon name="InformationCircleIcon" size={24} variant="solid" className="text-accent flex-shrink-0" />
                  <div>
                    <h3 className="font-headline font-semibold text-xl text-primary mb-4">
                      Compliance Responsibility
                    </h3>
                    <p className="font-body text-sm text-text-secondary leading-relaxed mb-4">
                      Danny Clans Media provides compliance frameworks and documentation to support client advertising activities. However, ultimate responsibility for regulatory compliance rests with the client. Our service includes:
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-primary">Compliance framework documentation and templates</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-primary">Platform policy monitoring and update notifications</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-primary">Regulatory change analysis and impact assessment</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-text-primary">Professional consultation on compliance requirements</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}